import dayjs, { Dayjs } from "dayjs";
import {
  DailyExpenseGapBranch,
  DailyExpenseReviewRow,
  DailyExpenseReviewStatus,
  DailyExpenseSkipped,
  DailyExpenseStatus,
} from "../../types/daily-expense.type";
import { numberWithCommas } from "../../utils";
import {
  API_DATE_FORMAT,
  DAILY_EXPENSE_REVIEW_PATH,
  DISPLAY_DATE_FORMAT,
  REVIEW_LINK_PARAM,
  REVIEW_MAX_DAYS,
} from "./constants";

/** The สถานะ filter's ทั้งหมด: no status sent, so the server lists all three it reviews. */
export const ALL_STATUSES = "ALL";
export type ReviewStatusFilter = DailyExpenseReviewStatus | typeof ALL_STATUSES;

/** รออนุมัติ first: it is the default and the work list. */
export const REVIEW_STATUS_OPTIONS: { value: ReviewStatusFilter; label: string }[] = [
  { value: "SUBMITTED", label: "รออนุมัติ" },
  { value: "APPROVED", label: "อนุมัติ" },
  { value: "REJECTED", label: "ตีกลับ" },
  { value: ALL_STATUSES, label: "ทั้งหมด" },
];

const LIST_TITLE: Record<ReviewStatusFilter, string> = {
  SUBMITTED: "เอกสารรออนุมัติ",
  APPROVED: "เอกสารอนุมัติแล้ว",
  REJECTED: "เอกสารตีกลับ",
  [ALL_STATUSES]: "เอกสารทุกสถานะ",
};

export const listTitleOf = (status: ReviewStatusFilter) => LIST_TITLE[status];

export const emptyListTitleOf = (status: ReviewStatusFilter) =>
  status === ALL_STATUSES
    ? "ไม่มีเอกสารในช่วงที่เลือก"
    : `ไม่มี${LIST_TITLE[status]}ในช่วงที่เลือก`;

/** Both ends counted, so 1–31 October is exactly the server's cap. */
export const isReviewRangeTooLong = (dateFrom: Dayjs, dateTo: Dayjs): boolean =>
  dateTo.startOf("day").diff(dateFrom.startOf("day"), "day") + 1 > REVIEW_MAX_DAYS;

/**
 * Only a document waiting for head office can be approved in bulk, and not once its month is
 * closed; rejecting stays one document at a time because it needs a reason.
 */
export const isSelectableRow = (row: DailyExpenseReviewRow): boolean =>
  row.status === "SUBMITTED" && !row.is_locked;

export const selectableUuidsOf = (rows: DailyExpenseReviewRow[]): string[] =>
  rows.filter(isSelectableRow).map((row) => row.uuid);

/** The ticks that still point at a selectable row — a reload may have moved some on. */
export const keepSelectable = (
  selected: string[],
  rows: DailyExpenseReviewRow[],
): string[] => {
  const selectable = new Set(selectableUuidsOf(rows));
  return selected.filter((uuid) => selectable.has(uuid));
};

export const toggleUuid = (selected: string[], uuid: string): string[] =>
  selected.includes(uuid)
    ? selected.filter((candidate) => candidate !== uuid)
    : [...selected, uuid];

/** The header box: ticks every selectable row, or clears them once all are ticked. */
export const toggleAllSelectable = (
  selected: string[],
  rows: DailyExpenseReviewRow[],
): string[] => {
  const selectable = selectableUuidsOf(rows);
  const isAllSelected =
    selectable.length > 0 && selectable.every((uuid) => selected.includes(uuid));
  return isAllSelected ? [] : selectable;
};

export const bulkApproveMessage = (approvedCount: number, skippedCount: number) =>
  skippedCount > 0
    ? `อนุมัติแล้ว ${numberWithCommas(approvedCount)} ใบ · ข้าม ${numberWithCommas(skippedCount)} ใบ`
    : `อนุมัติแล้ว ${numberWithCommas(approvedCount)} ใบ`;

/** "03-KB · 19/10/2026 — reason" for a skipped document; the uuid alone if it left the list. */
export const skippedText = (
  skipped: DailyExpenseSkipped,
  rows: DailyExpenseReviewRow[],
): string => {
  const row = rows.find((candidate) => candidate.uuid === skipped.uuid);
  const subject = row
    ? `${row.branch_code} · ${dayjs(row.business_date).format(DISPLAY_DATE_FORMAT)}`
    : skipped.uuid;
  return `${subject} — ${skipped.message}`;
};

/** Every day of the range, in order: the columns of the branch × day view. */
export const datesBetween = (dateFrom: Dayjs, dateTo: Dayjs): string[] => {
  const dates: string[] = [];
  for (
    let date = dateFrom.startOf("day");
    !date.isAfter(dateTo, "day");
    date = date.add(1, "day")
  ) {
    dates.push(date.format(API_DATE_FORMAT));
  }
  return dates;
};

/** A cell of the branch × day view: no route ran (OFF), or the day's status (null = ยังไม่กรอก). */
export type GapCellKind = "OFF" | "NOT_STARTED" | DailyExpenseStatus;

export const gapCellKindOf = (
  branch: DailyExpenseGapBranch,
  businessDate: string,
): GapCellKind => {
  const day = branch.days.find((candidate) => candidate.business_date === businessDate);
  if (!day) return "OFF";
  return day.status ?? "NOT_STARTED";
};

/** A cell opens the document only once the branch has handed it over. */
export const isGapCellOpenable = (kind: GapCellKind): boolean =>
  kind === "SUBMITTED" || kind === "APPROVED" || kind === "REJECTED";

/** The branches with the most days still to hand over first, so the gaps lead the view. */
export const sortGapBranches = (gap: DailyExpenseGapBranch[]): DailyExpenseGapBranch[] =>
  [...gap].sort(
    (left, right) =>
      right.missing_count - left.missing_count ||
      left.branch_code.localeCompare(right.branch_code),
  );

/** รายการเอกสาร, or the branch × day view of the days not handed in yet. */
export type ReviewView = "LIST" | "GAP";

/** What a link into S3 may preset; anything absent keeps the screen's default. */
export interface ReviewLink {
  start?: string;
  end?: string;
  branchCode?: string;
  status?: ReviewStatusFilter;
  view?: ReviewView;
}

const REVIEW_VIEWS: ReviewView[] = ["LIST", "GAP"];

/** The S3 path with its filter in the query string (S4's warning strip links here). */
export const reviewPathOf = (link: ReviewLink): string => {
  const search = new URLSearchParams();
  if (link.start) search.set(REVIEW_LINK_PARAM.START, link.start);
  if (link.end) search.set(REVIEW_LINK_PARAM.END, link.end);
  if (link.branchCode) search.set(REVIEW_LINK_PARAM.BRANCH, link.branchCode);
  if (link.status) search.set(REVIEW_LINK_PARAM.STATUS, link.status);
  if (link.view) search.set(REVIEW_LINK_PARAM.VIEW, link.view);
  const query = search.toString();
  return query ? `${DAILY_EXPENSE_REVIEW_PATH}?${query}` : DAILY_EXPENSE_REVIEW_PATH;
};

/** A real calendar day spelled `YYYY-MM-DD` (2026-02-30 is not one). */
const isApiDate = (value: string | null): value is string =>
  value !== null && dayjs(value).format(API_DATE_FORMAT) === value;

/** A hand-edited query string presets only what it spells correctly; the rest is dropped. */
export const reviewLinkOf = (search: URLSearchParams): ReviewLink => {
  const start = search.get(REVIEW_LINK_PARAM.START);
  const end = search.get(REVIEW_LINK_PARAM.END);
  const status = search.get(REVIEW_LINK_PARAM.STATUS);
  const view = search.get(REVIEW_LINK_PARAM.VIEW);
  const isRangeValid = isApiDate(start) && isApiDate(end) && !dayjs(end).isBefore(dayjs(start));
  return {
    ...(isRangeValid ? { start, end } : {}),
    branchCode: search.get(REVIEW_LINK_PARAM.BRANCH) || undefined,
    status: REVIEW_STATUS_OPTIONS.find((option) => option.value === status)?.value,
    view: REVIEW_VIEWS.find((candidate) => candidate === view),
  };
};
